/**
 * Blade Performance — Predictive Search with instant client-side matching
 * ---------------------------------------------------------------------------
 * Replaces: assets/predictive-search.js
 * Companion file: templates/collection.search-index.liquid
 *
 * THE PROBLEM THIS SOLVES
 * Shopify's predictive endpoint needs a network round-trip per keystroke
 * (200-400ms on mobile) and has no typo tolerance at all. Verified live on
 * this store: "sno" returns 8 snow foams, but "snw foam" — one wrong letter —
 * returns absolutely nothing. Mid-word queries are worse: typing towards
 * "tyre shine", the query "tyre sh" returns a single wheel shampoo.
 *
 * THE APPROACH — two layers, and the fast one always wins the race:
 *
 *   LAYER 1 (instant, 0ms, no network)
 *     The catalogue is downloaded once per session as a compact JSON index
 *     and searched in the browser on EVERY keystroke. Scoring is prefix-aware
 *     and typo-tolerant (bounded edit distance), so results appear before the
 *     customer's finger leaves the key. If nothing matches, the same synonym
 *     rewrites the server ladder uses are applied locally — so "tyre shine"
 *     surfaces the four tyre dressings instantly, with no request at all.
 *
 *   LAYER 2 (server, debounced 150ms)
 *     Shopify's own endpoint still runs, because it contributes things the
 *     index cannot: collections, articles, and canonical relevance. When it
 *     returns results they replace the instant ones. When it returns nothing,
 *     the instant results STAY — the panel is never emptied.
 *
 * Net effect: the panel is populated within ~2ms of a keypress and only ever
 * gets richer. It never blanks, never flickers to a spinner, and never
 * dead-ends.
 *
 * ALSO IN THIS FILE
 *   - Full WAI-ARIA 1.2 combobox: arrow keys, Home/End, Enter, Escape, Tab,
 *     aria-activedescendant, and a polite live region announcing counts.
 *   - Four-tier server retry ladder (normalise → widen fields → synonym
 *     rewrite → merchandised no-results), with rewrites disclosed to the user.
 *   - Recent searches, popular-term chips that search in place.
 *   - Guarded init on DOM-ready; a missing drawer can no longer throw.
 *   - Dispatches `search:results` for analytics.
 *
 * Pure logic is exported at the bottom for the node test harness.
 */
(function () {
  'use strict';

  /* ================================================================== *
   *  SECTION 1 — Query intelligence (pure, unit-tested)
   * ================================================================== */

  // Per-word normalisation: US → UK spellings, common misspellings, and
  // compound words customers run together. Applied before every lookup,
  // client and server alike. Values may contain spaces (compound splits).
  var NORMALISE = {
    // US → UK spellings
    tire: 'tyre',
    tires: 'tyres',
    microfiber: 'microfibre',
    microfibers: 'microfibres',
    fiber: 'fibre',
    fibers: 'fibres',
    color: 'colour',
    colors: 'colours',
    armor: 'armour',
    armorall: 'armourall',
    odor: 'odour',
    odors: 'odours',
    aluminum: 'aluminium',
    rotor: 'disc',
    rotors: 'discs',
    windshield: 'windscreen',
    // Misspellings — ceramic
    ceramick: 'ceramic',
    cermic: 'ceramic',
    ceramik: 'ceramic',
    seramic: 'ceramic',
    cermaic: 'ceramic',
    cerramic: 'ceramic',
    // Misspellings — shampoo
    shampo: 'shampoo',
    shampooo: 'shampoo',
    shampu: 'shampoo',
    shamppo: 'shampoo',
    // Misspellings — caliper (the UK double-l is also folded)
    calliper: 'caliper',
    callipers: 'calipers',
    calipur: 'caliper',
    // Misspellings — general car care
    wheal: 'wheel',
    weel: 'wheel',
    wheals: 'wheels',
    weels: 'wheels',
    cleener: 'cleaner',
    clener: 'cleaner',
    cleanr: 'cleaner',
    pollish: 'polish',
    polsih: 'polish',
    polishh: 'polish',
    dresing: 'dressing',
    dressin: 'dressing',
    presure: 'pressure',
    preasure: 'pressure',
    towell: 'towel',
    towells: 'towels',
    towl: 'towel',
    towles: 'towels',
    freshner: 'freshener',
    freshners: 'fresheners',
    freshiner: 'freshener',
    freshenner: 'freshener',
    detailling: 'detailing',
    detalling: 'detailing',
    detaling: 'detailing',
    interier: 'interior',
    exhuast: 'exhaust',
    exaust: 'exhaust',
    stiker: 'sticker',
    stikers: 'stickers',
    accesories: 'accessories',
    accessorys: 'accessories',
    waxs: 'wax',
    sealent: 'sealant',
    selant: 'sealant',
    vynil: 'vinyl',
    vinal: 'vinyl',
    mircofibre: 'microfibre',
    micofibre: 'microfibre',
    // Compound words run together → split
    snowfoam: 'snow foam',
    snofoam: 'snow foam',
    snowform: 'snow foam',
    airfreshener: 'air freshener',
    airfreshner: 'air freshener',
    airfresh: 'air freshener',
    numberplate: 'number plate',
    numberplates: 'number plates',
    showplate: 'show plate',
    showplates: 'show plates',
    screenwash: 'screen wash',
    wheelcleaner: 'wheel cleaner',
    wheelcleaners: 'wheel cleaners',
    tyreshine: 'tyre shine',
    tireshine: 'tyre shine'
  };

  // Synonym rewrites. One word is swapped at a time so intent is preserved —
  // "tyre shine" becomes "tyre dressing", never just "shine" (which returns
  // glass shine and wax, all wrong).
  var SYNONYMS = {
    // Finish & shine
    shine: ['dressing', 'gloss'],
    shiner: ['dressing'],
    dressing: ['shine', 'gloss'],
    buff: ['polish'],
    buffer: ['polish'],
    polisher: ['polish'],
    polishers: ['polish'],
    // Washing
    soap: ['shampoo'],
    suds: ['shampoo', 'snow foam'],
    wash: ['shampoo'],
    sponge: ['noodle', 'wash'],
    mitt: ['noodle', 'wash'],
    bucket: ['wash'],
    lance: ['foam'],
    // Wheels — UK slang included
    rim: ['wheel'],
    rims: ['wheels'],
    alloy: ['wheel'],
    alloys: ['wheels'],
    mags: ['wheels'],
    // Scent
    scent: ['air freshener'],
    smell: ['air freshener'],
    perfume: ['air freshener'],
    deodoriser: ['air freshener'],
    freshie: ['air freshener'],
    freshies: ['air freshener'],
    smellies: ['air freshener'],
    // Cloths & drying
    cloth: ['microfibre'],
    cloths: ['microfibre'],
    clothes: ['microfibre'],
    rag: ['microfibre'],
    rags: ['microfibre'],
    towel: ['microfibre'],
    chamois: ['drying towel'],
    shammy: ['drying towel'],
    // Glass
    windscreen: ['glass'],
    window: ['glass'],
    windows: ['glass'],
    // Decontamination & protection
    iron: ['fallout'],
    fallout: ['iron'],
    clay: ['clay bar'],
    sealant: ['ceramic', 'wax'],
    degreaser: ['cleaner'],
    apc: ['cleaner'],
    bug: ['tar'],
    sap: ['tar'],
    protection: ['coating'],
    // Verified live: the store stocks caliper paint, not brake discs, so a
    // normalised "discs" query finds nothing until rewritten to "brake".
    disc: ['brake'],
    discs: ['brake'],
    // Stickers & general
    decal: ['sticker'],
    decals: ['stickers'],
    valet: ['detailing'],
    valeting: ['detailing']
  };

  function normaliseQuery(raw) {
    return String(raw || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map(function (word) {
        return Object.prototype.hasOwnProperty.call(NORMALISE, word) ? NORMALISE[word] : word;
      })
      .join(' ');
  }

  // Candidate rewrites for a failing query, best-first:
  //  1. each single-word synonym substitution (intent preserved)
  //  2. individual words, longest first (last resort)
  function buildRewrites(normalised) {
    var words = normalised.split(' ').filter(Boolean);
    var out = [];
    var seen = {};
    function push(q) {
      if (q && q !== normalised && !seen[q]) {
        seen[q] = true;
        out.push(q);
      }
    }
    words.forEach(function (word, i) {
      (SYNONYMS[word] || []).forEach(function (alt) {
        var next = words.slice();
        next[i] = alt;
        push(next.join(' '));
      });
    });
    if (words.length > 1) {
      words
        .slice()
        .sort(function (a, b) { return b.length - a.length; })
        .forEach(push);
    }
    return out;
  }

  function tokenize(text) {
    return String(text || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(function (t) { return t.length > 0; });
  }

  /**
   * Light singular/plural fold so "wheels" and "wheel" (or "accessories" and
   * "accessory") count as the same word. Deliberately conservative:
   * words of 3 letters or fewer and words ending in a double-s ("glass",
   * "gloss") are left alone, because trimming those changes the word.
   */
  function stem(t) {
    if (t.length > 4 && t.slice(-3) === 'ies') return t.slice(0, -3) + 'y';
    if (t.length > 3 && t.charAt(t.length - 1) === 's' && t.charAt(t.length - 2) !== 's') {
      return t.slice(0, -1);
    }
    return t;
  }

  /**
   * Bounded Damerau-style edit distance check. Returns true if `a` can be
   * turned into `b` within `max` edits. Bails out as soon as the best possible
   * remaining distance exceeds max, so the common "nowhere near" case costs
   * almost nothing — which matters when this runs across the whole catalogue
   * on every keypress.
   */
  function withinEditDistance(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return false;
    if (la === 0) return lb <= max;
    if (lb === 0) return la <= max;

    // Three rolling rows: d2 is row i-2 (needed only for transposition),
    // d1 is row i-1, d0 is the row being filled.
    var d2 = null;
    var d1 = new Array(lb + 1);
    var d0 = new Array(lb + 1);
    var i, j;
    for (j = 0; j <= lb; j++) d1[j] = j;

    for (i = 1; i <= la; i++) {
      d0[0] = i;
      var rowMin = i;
      var ac = a.charCodeAt(i - 1);
      for (j = 1; j <= lb; j++) {
        var cost = ac === b.charCodeAt(j - 1) ? 0 : 1;
        var v = d1[j] + 1;                    // deletion
        var ins = d0[j - 1] + 1;              // insertion
        if (ins < v) v = ins;
        var sub = d1[j - 1] + cost;           // substitution
        if (sub < v) v = sub;
        // Transposition ("sonw" → "snow"), the most common real typo.
        if (d2 && i > 1 && j > 1 &&
            ac === b.charCodeAt(j - 2) &&
            a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
          var trans = d2[j - 2] + 1;
          if (trans < v) v = trans;
        }
        d0[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return false; // Early exit — cannot recover.
      var spare = d2;
      d2 = d1;
      d1 = d0;
      d0 = spare || new Array(lb + 1);
    }
    return d1[lb] <= max;
  }

  // How many typos we forgive, scaled to word length. Short words get no
  // slack — "wax" and "was" are different products, not a typo.
  function allowedTypos(len) {
    if (len <= 3) return 0;
    if (len <= 5) return 1;
    return 2;
  }

  /* ================================================================== *
   *  SECTION 2 — Instant index: scoring
   * ================================================================== */

  var W_TITLE = 3, W_TYPE = 2, W_VENDOR = 2, W_TAG = 1;

  /**
   * Turn a raw index record into a scoreable one. Done once at load so the
   * per-keystroke path only compares pre-lowercased strings.
   */
  function prepareRecord(raw) {
    var title = raw.t || '';
    var fields = [];
    tokenize(title).forEach(function (t) { fields.push([t, W_TITLE]); });
    tokenize(raw.y).forEach(function (t) { fields.push([t, W_TYPE]); });
    tokenize(raw.v).forEach(function (t) { fields.push([t, W_VENDOR]); });
    tokenize(raw.g).forEach(function (t) { fields.push([t, W_TAG]); });
    return {
      title: title,
      titleLower: title.toLowerCase(),
      handle: raw.h || '',
      image: raw.i || '',
      price: raw.p || '',
      vendor: raw.v || '',
      available: raw.a !== false,
      tokens: fields
    };
  }

  /**
   * Score one product against the query's tokens.
   *
   * AND semantics: EVERY query token must find something in the product, which
   * is what stops "snow foam" returning every product containing "foam". A
   * token can match exactly, by prefix (so results appear mid-word), by
   * substring, or fuzzily within the typo budget — in descending value.
   * Returns 0 when the product is not a match at all.
   */
  function scoreRecord(record, queryTokens, fullQuery, relaxed) {
    // Two-pass design. The strict pass forgives typos only on words of 4+
    // letters, because at three letters a "typo" is usually a different word
    // ("was" vs "wax"). The relaxed pass is run ONLY when the strict pass
    // returned nothing at all, which is what rescues short typos like
    // "snw" → "snow" without ever letting them hijack a query that already
    // had good matches.
    var fuzzyMin = relaxed ? 3 : 4;
    var total = 0;
    for (var q = 0; q < queryTokens.length; q++) {
      var qt = queryTokens[q];
      var best = 0;
      for (var f = 0; f < record.tokens.length; f++) {
        var pt = record.tokens[f][0];
        var w = record.tokens[f][1];
        var s = 0;
        if (pt === qt) {
          s = 10 * w;
        } else if (stem(pt) === stem(qt)) {
          // Singular/plural fold: "wheels" ↔ "wheel" is a near-exact match.
          s = 9 * w;
        } else if (pt.length > qt.length && pt.lastIndexOf(qt, 0) === 0) {
          // Prefix: "sno" matches "snow". This is what makes typing feel live.
          s = 8 * w;
        } else if (qt.length >= 3 && pt.indexOf(qt) > 0) {
          s = 4 * w;
        } else if (qt.length >= fuzzyMin &&
                   withinEditDistance(qt, pt, relaxed ? Math.max(1, allowedTypos(qt.length)) : allowedTypos(qt.length))) {
          // Typo rescue: "snw" → "snow", "shampo" → "shampoo".
          s = 3 * w;
        }
        if (s > best) {
          best = s;
          if (best === 10 * W_TITLE) break; // Cannot do better for this token.
        }
      }
      if (best === 0) return 0; // One unmatched token disqualifies the product.
      total += best;
    }

    // Whole-phrase bonuses: a title that literally begins with what was typed
    // is almost always the intended product.
    if (fullQuery) {
      if (record.titleLower.lastIndexOf(fullQuery, 0) === 0) total += 25;
      else if (record.titleLower.indexOf(fullQuery) > -1) total += 12;
    }
    // Never rank something they cannot buy above something they can.
    if (record.available) total += 4;
    // Shorter titles are usually the "main" product rather than a variant pack.
    total += Math.max(0, 6 - Math.floor(record.title.length / 12));

    return total;
  }

  /**
   * Search prepared records. Falls back to local synonym rewrites when the
   * literal query matches nothing, so intent-level misses are recovered with
   * zero latency rather than waiting on the server ladder.
   */
  function searchRecords(records, rawQuery, limit) {
    var normalised = normaliseQuery(rawQuery);
    if (!normalised) return { items: [], rewrittenAs: null };

    var attempt = function (queryString, relaxed) {
      var qTokens = tokenize(queryString);
      if (!qTokens.length) return [];
      var hits = [];
      for (var i = 0; i < records.length; i++) {
        var score = scoreRecord(records[i], qTokens, queryString, relaxed);
        if (score > 0) hits.push({ record: records[i], score: score, index: i });
      }
      hits.sort(function (a, b) {
        if (b.score !== a.score) return b.score - a.score;
        return a.index - b.index; // Stable: preserves catalogue order on ties.
      });
      return hits;
    };

    // 1. Strict. 2. Relaxed short-word typos. Only then 3. synonym rewrites,
    // because a rewrite changes what the customer asked for and should always
    // be the last resort — and is disclosed to them when it happens.
    var hits = attempt(normalised, false);
    if (!hits.length) hits = attempt(normalised, true);
    if (hits.length) {
      return { items: hits.slice(0, limit), rewrittenAs: null, query: normalised };
    }

    var rewrites = buildRewrites(normalised);
    for (var r = 0; r < rewrites.length; r++) {
      var alt = attempt(rewrites[r], false);
      if (!alt.length) alt = attempt(rewrites[r], true);
      if (alt.length) {
        return { items: alt.slice(0, limit), rewrittenAs: rewrites[r], query: rewrites[r] };
      }
    }
    return { items: [], rewrittenAs: null, query: normalised };
  }

  /* ================================================================== *
   *  SECTION 3 — Index loading
   * ================================================================== */

  var INDEX_URL = '/collections/all?view=search-index';
  var INDEX_CACHE_KEY = 'bp:search-index:v1';

  function InstantIndex() {
    this.records = [];
    this.loading = false;
    this.loaded = false;
    this.onUpdate = null;
  }

  InstantIndex.prototype.ready = function () {
    return this.records.length > 0;
  };

  /** Idempotent — safe to call on every drawer open. */
  InstantIndex.prototype.load = function () {
    if (this.loading || this.loaded) return;
    this.loading = true;

    // Session cache first: a returning customer pays nothing.
    try {
      var cached = sessionStorage.getItem(INDEX_CACHE_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          this.ingest(parsed);
          this.loaded = true;
          this.loading = false;
          return;
        }
      }
    } catch (e) { /* Private mode or corrupt entry — just refetch. */ }

    var self = this;
    var collected = [];

    function fetchPage(page) {
      return fetch(INDEX_URL + '&page=' + page, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.json();
        })
        .then(function (data) {
          if (!data || !Array.isArray(data.products)) throw new Error('bad index');
          collected = collected.concat(data.products);
          // Ingest progressively: page 1 is searchable while 2 and 3 download.
          self.ingest(data.products);
          if (page < data.pages && page < 8) return fetchPage(page + 1);
          return null;
        });
    }

    fetchPage(1)
      .then(function () {
        self.loaded = true;
        self.loading = false;
        try {
          sessionStorage.setItem(INDEX_CACHE_KEY, JSON.stringify(collected));
        } catch (e) { /* Over quota — the index still works in memory. */ }
      })
      .catch(function () {
        // Index unavailable: the server layer alone still delivers a working
        // search, just without the instant tier. Degrade, never break.
        self.loading = false;
      });
  };

  InstantIndex.prototype.ingest = function (rawList) {
    for (var i = 0; i < rawList.length; i++) {
      this.records.push(prepareRecord(rawList[i]));
    }
    if (typeof this.onUpdate === 'function') this.onUpdate();
  };

  InstantIndex.prototype.search = function (query, limit) {
    return searchRecords(this.records, query, limit || 8);
  };

  /* ================================================================== *
   *  SECTION 4 — Markup helpers
   * ================================================================== */

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegex(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Bold the parts of a title the customer actually typed. Matching the
   * server's `highlight` filter output means instant and server results look
   * identical — no visual jump when one replaces the other.
   */
  function highlightTitle(title, queryTokens) {
    var out = escapeHtml(title);
    queryTokens.forEach(function (token) {
      if (token.length < 1) return;
      var re = new RegExp('(' + escapeRegex(escapeHtml(token)) + ')', 'ig');
      out = out.replace(re, '<strong>$1</strong>');
    });
    return out;
  }

  function renderInstantMarkup(result, typedTerm) {
    var queryTokens = tokenize(result.query || typedTerm);
    var parts = [];
    parts.push('<div class="psr" data-psr-root data-psr-instant data-psr-count="' + result.items.length + '">');

    if (result.rewrittenAs) {
      parts.push(
        '<div class="psr__relaxed">No exact match for <strong>' +
        escapeHtml(typedTerm) + '</strong>. Showing results for <strong>' +
        escapeHtml(result.rewrittenAs) + '</strong>.</div>'
      );
    }

    parts.push('<div class="side-panel-content--inner psr__inner">');
    parts.push('<ul class="psr__listbox" role="listbox" aria-label="Search results">');
    parts.push('<li class="psr__group" role="group" aria-label="Products">');
    parts.push('<span class="psr__group-title" aria-hidden="true">Products</span>');

    result.items.forEach(function (hit) {
      var r = hit.record;
      parts.push(
        '<a href="/products/' + encodeURIComponent(r.handle) + '" ' +
        'class="psr__option psr__option--product" role="option" data-psr-option ' +
        'title="' + escapeHtml(r.title) + '">' +
        '<span class="psr__option-icon" aria-hidden="true">' +
        (r.image
          ? '<img src="' + escapeHtml(r.image) + '" width="72" height="72" loading="lazy" alt="">'
          : '<span class="psr__option-icon-fallback"></span>') +
        '</span>' +
        '<span class="psr__option-body">' +
        '<span class="psr__option-title">' + highlightTitle(r.title, queryTokens) + '</span>' +
        (r.vendor ? '<span class="psr__option-meta">' + escapeHtml(r.vendor) + '</span>' : '') +
        '<span class="psr__option-price">' + escapeHtml(r.price) +
        (r.available ? '' : '<span class="psr__option-oos">Out of stock</span>') +
        '</span>' +
        '</span></a>'
      );
    });

    parts.push('</li></ul></div>');
    parts.push(
      '<div class="side-panel-content--search-footer">' +
      '<button type="button" class="button full" id="search-results-submit">' +
      '<span>View all results</span></button></div>'
    );
    parts.push('</div>');
    return parts.join('');
  }

  /* ================================================================== *
   *  SECTION 5 — Component
   * ================================================================== */

  var MIN_CHARS = 1;          // Instant tier is free, so start at one letter.
  var SERVER_MIN_CHARS = 2;   // Network requests still wait for two.
  var DEBOUNCE_MS = 150;
  var CACHE_MAX = 50;
  var INSTANT_LIMIT = 8;
  var RECENT_KEY = 'bp:recent-searches';
  var RECENT_MAX = 5;

  var FIELDS_DEFAULT = 'title,product_type,vendor,variants.title,variants.sku';
  var FIELDS_WIDE = FIELDS_DEFAULT + ',body';

  /**
   * What opens the search drawer.
   *
   * `.thb-quick-search` is the Fuel theme's native trigger, but this store's
   * live header is a custom section (ss_header_15) whose search icon is a
   * plain <a href="/search"> with a `header-search-*` class. Nothing on the
   * site matched the native selector, which meant the predictive drawer was
   * unreachable — customers clicking search got a full page load to /search
   * and never saw predictive results at all.
   *
   * Matching both selectors makes the drawer work on this header without
   * editing the header section. The data attribute is there so any future
   * header can opt in by adding it to an element.
   *
   * Fallback is intact: if OverlayManager is missing the click is NOT
   * intercepted, so the icon still navigates to /search exactly as it does
   * today. To revert to that behaviour entirely, delete the two selectors
   * after `.thb-quick-search` below.
   */
  var TRIGGER_SELECTOR = '.thb-quick-search, a[class*="header-search"], [data-search-drawer-toggle]';

  function localDebounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function PredictiveSearch() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return; // Guarded: never take down the page.

    this.form = this.container.querySelector('form.searchform');
    this.input = this.container.querySelector('input[type="search"]');
    this.buttons = document.querySelectorAll(TRIGGER_SELECTOR);
    this.defaultTab = this.container.querySelector('.side-panel-content--initial');
    this.panel = this.container.querySelector('.side-panel-content--has-tabs');
    if (!this.form || !this.input || !this.panel) return;

    this.cache = new Map();
    this.controller = null;
    this.searchSeq = 0;
    this.activeIndex = -1;
    this.options = [];
    this.hasInstantResults = false;

    this.index = new InstantIndex();

    this.setupCombobox();
    this.setupLiveRegion();
    this.setupEventListeners();
    this.renderRecentSearches();
  }

  /* ----------------------------- setup ------------------------------ */

  PredictiveSearch.prototype.setupCombobox = function () {
    var panelId = this.panel.id || 'predictive-search-panel';
    this.panel.id = panelId;
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', panelId);
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
  };

  PredictiveSearch.prototype.setupLiveRegion = function () {
    this.liveRegion = document.createElement('div');
    this.liveRegion.className = 'visually-hidden';
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('role', 'status');
    this.container.appendChild(this.liveRegion);
  };

  PredictiveSearch.prototype.setupEventListeners = function () {
    var self = this;
    var debounceFn = (typeof debounce === 'function' ? debounce : localDebounce);

    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    // Two separate paths on purpose: the instant tier runs synchronously on
    // every keystroke, and only the network call is debounced.
    this.debouncedServer = debounceFn(function (term) {
      self.runLadder(term);
    }, DEBOUNCE_MS);

    this.input.addEventListener('input', function () { self.onInput(); });

    // Warm the index the moment the customer shows intent to search.
    this.input.addEventListener('focus', function () { self.index.load(); }, { once: true });

    this.buttons.forEach(function (item) {
      item.addEventListener('click', function (event) {
        // Only hijack the click if the drawer can actually be opened.
        // Otherwise let the browser follow the link to /search, which is a
        // perfectly good fallback rather than a dead icon.
        if (typeof OverlayManager === 'undefined') return;
        event.preventDefault();
        self.index.load();
        if (OverlayManager.isOpen(self.container)) {
          OverlayManager.close(self.container);
        } else {
          self.stampPromotionTemplate();
          OverlayManager.open(self.container);
          setTimeout(function () {
            self.input.focus({ preventScroll: true });
          }, 100);
          if (typeof dispatchCustomEvent === 'function') dispatchCustomEvent('search:open');
        }
      });
    });

    // Delegated: "view all results", term chips, and recent-search saving.
    this.container.addEventListener('click', function (event) {
      var submitButton = event.target.closest('#search-results-submit');
      if (submitButton) {
        self.submitForm();
        return;
      }
      var chip = event.target.closest('[data-psr-term]');
      if (chip) {
        event.preventDefault();
        self.runTerm(chip.getAttribute('data-psr-term'));
        return;
      }
      var option = event.target.closest('[data-psr-option]');
      if (option) self.saveRecent(self.getQuery());
    });

    this.panel.addEventListener('mouseover', function (event) {
      var option = event.target.closest('[data-psr-option]');
      if (option) {
        var idx = self.options.indexOf(option);
        if (idx > -1) self.setActive(idx, { scroll: false });
      }
    });
  };

  /* --------------------------- interaction --------------------------- */

  PredictiveSearch.prototype.getQuery = function () {
    return this.input.value.trim();
  };

  PredictiveSearch.prototype.runTerm = function (term) {
    if (!term) return;
    this.input.value = term;
    this.input.focus({ preventScroll: true });
    this.onInput();
  };

  PredictiveSearch.prototype.onInput = function () {
    var term = this.getQuery();

    if (term.length < MIN_CHARS) {
      this.closePanel();
      return;
    }

    this.panel.classList.add('active');
    this.input.setAttribute('aria-expanded', 'true');

    // Invalidate any in-flight server ladder from the PREVIOUS keystroke
    // right now, not at the debounced moment. A slow multi-tier ladder can
    // outlive the 150ms debounce window, and without this its late responses
    // would overwrite the fresh instant results below with stale ones.
    this.searchSeq++;
    if (this.controller) this.controller.abort();

    // LAYER 1 — synchronous, ~2ms, no network.
    this.renderInstant(term);

    // LAYER 2 — debounced network request.
    if (term.length >= SERVER_MIN_CHARS) {
      this.debouncedServer(term);
    }
  };

  PredictiveSearch.prototype.onFormSubmit = function (event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }
    // If a rewrite produced the visible results, search the term that works.
    if (this.effectiveTerm && this.effectiveTerm !== normaliseQuery(this.getQuery())) {
      this.input.value = this.effectiveTerm;
    }
    this.saveRecent(this.getQuery());
  };

  PredictiveSearch.prototype.submitForm = function () {
    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
    } else {
      this.form.submit();
    }
  };

  PredictiveSearch.prototype.onKeydown = function (event) {
    var open = this.panel.classList.contains('active');
    switch (event.key) {
      case 'ArrowDown':
        if (!open) return;
        event.preventDefault();
        this.setActive(this.activeIndex >= this.options.length - 1 ? 0 : this.activeIndex + 1);
        break;
      case 'ArrowUp':
        if (!open) return;
        event.preventDefault();
        this.setActive(this.activeIndex <= 0 ? this.options.length - 1 : this.activeIndex - 1);
        break;
      case 'Home':
        if (!open || !this.options.length) return;
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        if (!open || !this.options.length) return;
        event.preventDefault();
        this.setActive(this.options.length - 1);
        break;
      case 'Enter':
        if (open && this.activeIndex > -1 && this.options[this.activeIndex]) {
          event.preventDefault();
          this.saveRecent(this.getQuery());
          var href = this.options[this.activeIndex].getAttribute('href');
          if (href) window.location.assign(href);
        }
        // Otherwise the form submits naturally → full results page.
        break;
      case 'Escape':
        if (open) {
          event.stopPropagation(); // First Escape clears; second closes drawer.
          this.closePanel();
        }
        break;
      case 'Tab':
        this.setActive(-1);
        break;
    }
  };

  PredictiveSearch.prototype.setActive = function (index, opts) {
    var prev = this.options[this.activeIndex];
    if (prev) prev.classList.remove('is-active');
    this.activeIndex = index;
    var next = this.options[index];
    if (next) {
      next.classList.add('is-active');
      this.input.setAttribute('aria-activedescendant', next.id);
      if (!opts || opts.scroll !== false) {
        next.scrollIntoView({ block: 'nearest' });
      }
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  };

  /* ------------------------ LAYER 1: instant ------------------------ */

  PredictiveSearch.prototype.renderInstant = function (term) {
    if (!this.index.ready()) return; // Index still downloading — server covers it.

    var result = this.index.search(term, INSTANT_LIMIT);
    if (!result.items.length) {
      // Do not blank the panel on a miss: the server ladder may still find
      // something, and if it does not, its merchandised no-results state is
      // a better ending than an empty box.
      this.hasInstantResults = false;
      return;
    }

    this.panel.classList.remove('loading');
    this.panel.innerHTML = renderInstantMarkup(result, term);
    this.hasInstantResults = true;
    this.effectiveTerm = result.rewrittenAs || normaliseQuery(term);
    this.afterRender();
    this.announce(result.items.length, this.effectiveTerm);
  };

  /* ------------------- LAYER 2: server retry ladder ------------------ */

  PredictiveSearch.prototype.runLadder = function (rawTerm) {
    var self = this;
    var seq = ++this.searchSeq;
    var normalised = normaliseQuery(rawTerm);

    var attempts = [
      { q: normalised, fields: FIELDS_DEFAULT, rewritten: false },
      { q: normalised, fields: FIELDS_WIDE, rewritten: false }
    ];
    buildRewrites(normalised).forEach(function (q) {
      attempts.push({ q: q, fields: FIELDS_DEFAULT, rewritten: true });
    });

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    var signal = this.controller.signal;

    // Only show the spinner when there is nothing on screen to look at.
    if (!this.hasInstantResults) this.panel.classList.add('loading');

    var firstResult = null; // Tier-1 markup, reused for the no-results state.

    function step(i) {
      if (seq !== self.searchSeq) return; // A newer keystroke took over.
      if (i >= attempts.length) {
        self.panel.classList.remove('loading');
        // Everything failed. If the instant tier found something, leave it
        // alone — closest matches beat an apology.
        if (self.hasInstantResults) return;
        self.render(firstResult || { markup: '', count: 0 }, normalised, false);
        return;
      }
      var attempt = attempts[i];
      self.fetchResults(attempt.q, attempt.fields, signal)
        .then(function (result) {
          if (seq !== self.searchSeq) return;
          if (i === 0) firstResult = result;
          if (result.count > 0) {
            // A REWRITTEN server result must never replace instant results:
            // the instant tier corrected the typo in place ("snw foam" →
            // snow foams), while the server rewrite is cruder ("foam" —
            // which drags in foam applicators). Only an exact/normalised
            // server match outranks what is already on screen.
            if (attempt.rewritten && self.hasInstantResults) {
              self.panel.classList.remove('loading');
              return;
            }
            self.render(result, normalised, attempt.rewritten ? attempt.q : false);
          } else {
            step(i + 1);
          }
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (seq !== self.searchSeq) return;
          self.panel.classList.remove('loading');
          // A failed request must not wipe good instant results.
          if (!self.hasInstantResults) self.renderError();
        });
    }
    step(0);
  };

  PredictiveSearch.prototype.fetchResults = function (query, fields, signal) {
    var self = this;
    var cacheKey = query + '|' + fields;
    if (this.cache.has(cacheKey)) {
      return Promise.resolve(this.cache.get(cacheKey));
    }
    var routes = (typeof theme !== 'undefined' && theme.routes) || {};
    var url = routes.predictive_search_url || '/search/suggest';
    var params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,article,query',
      'resources[limit]': '10',
      'resources[options][fields]': fields,
      section_id: 'predictive-search'
    });
    return fetch(url + '?' + params.toString(), { signal: signal })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.querySelector('#shopify-section-predictive-search');
        var markup = section ? section.innerHTML : '';
        var root = section && section.querySelector('[data-psr-root]');
        var count = root ? parseInt(root.getAttribute('data-psr-count'), 10) || 0 : 0;
        var result = { markup: markup, count: count };
        if (self.cache.size >= CACHE_MAX) {
          self.cache.delete(self.cache.keys().next().value);
        }
        self.cache.set(cacheKey, result);
        return result;
      });
  };

  /* ---------------------------- rendering ---------------------------- */

  PredictiveSearch.prototype.render = function (result, typedTerm, rewrittenTerm) {
    this.panel.classList.remove('loading');
    this.panel.innerHTML = result.markup;
    this.effectiveTerm = rewrittenTerm || typedTerm;

    // Disclose a rewrite plainly rather than silently substituting.
    if (rewrittenTerm && result.count > 0) {
      var notice = document.createElement('div');
      notice.className = 'psr__relaxed';
      notice.innerHTML = 'No exact match for <strong></strong>. Showing results for <strong></strong>.';
      var strongs = notice.querySelectorAll('strong');
      strongs[0].textContent = typedTerm;
      strongs[1].textContent = rewrittenTerm;
      var root = this.panel.querySelector('[data-psr-root]');
      if (root) root.insertBefore(notice, root.firstChild);
    }

    this.afterRender();
    this.announce(result.count, this.effectiveTerm);

    if (typeof dispatchCustomEvent === 'function') {
      dispatchCustomEvent('search:results', {
        term: typedTerm,
        effectiveTerm: this.effectiveTerm,
        count: result.count
      });
    }
  };

  /** Shared bookkeeping so instant and server results behave identically. */
  PredictiveSearch.prototype.afterRender = function () {
    this.options = Array.prototype.slice.call(this.panel.querySelectorAll('[data-psr-option]'));
    this.options.forEach(function (option, i) {
      option.id = 'psr-option-' + i;
    });
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'true');
  };

  PredictiveSearch.prototype.renderError = function () {
    this.panel.classList.remove('loading');
    this.panel.innerHTML =
      '<div class="psr"><div class="psr__message">' +
      '<p class="psr__empty-title">Search is temporarily unavailable.</p>' +
      '<p class="psr__empty-help">Press Enter to search the full store instead.</p>' +
      '</div></div>';
    this.options = [];
    this.activeIndex = -1;
  };

  PredictiveSearch.prototype.announce = function (count, term) {
    this.liveRegion.textContent = count > 0
      ? count + (count === 1 ? ' result for ' : ' results for ') + term
      : 'No results for ' + term + '. Suggested categories are shown.';
  };

  PredictiveSearch.prototype.closePanel = function () {
    this.panel.classList.remove('active', 'loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.options = [];
    this.activeIndex = -1;
    this.hasInstantResults = false;
    if (this.controller) this.controller.abort();
    this.searchSeq++;
  };

  /* ------------------------- recent searches ------------------------- */

  PredictiveSearch.prototype.getRecent = function () {
    try {
      var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  PredictiveSearch.prototype.saveRecent = function (term) {
    term = (term || '').trim();
    if (term.length < SERVER_MIN_CHARS) return;
    try {
      var list = this.getRecent().filter(function (t) {
        return t.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* Private mode — recents are a nicety, not a dependency. */ }
  };

  PredictiveSearch.prototype.renderRecentSearches = function () {
    var recent = this.getRecent();
    if (!recent.length || !this.defaultTab) return;
    var host = this.defaultTab.querySelector('.thb-predictive-search');
    if (!host || host.querySelector('.psr__recent')) return;

    var wrap = document.createElement('div');
    wrap.className = 'thb-predictive-search--popular psr__recent';
    var label = document.createElement('span');
    label.className = 'thb-predictive-search--label';
    label.textContent = 'Recent searches';
    wrap.appendChild(label);
    var row = document.createElement('div');
    recent.forEach(function (term) {
      var a = document.createElement('a');
      a.href = '#';
      a.setAttribute('data-psr-term', term);
      a.textContent = term;
      row.appendChild(a);
    });
    wrap.appendChild(row);
    host.insertBefore(wrap, host.firstChild);
  };

  /* ------------------------------ misc ------------------------------- */

  PredictiveSearch.prototype.stampPromotionTemplate = function () {
    var tpl = this.container.querySelector('.thb-predictive-search--promotion-template');
    if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
  };

  /* ------------------------------ init ------------------------------- */

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { new PredictiveSearch(); });
    } else {
      new PredictiveSearch();
    }
  }

  /* ------------------------ node test exports ------------------------ */

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      normaliseQuery: normaliseQuery,
      buildRewrites: buildRewrites,
      tokenize: tokenize,
      stem: stem,
      withinEditDistance: withinEditDistance,
      allowedTypos: allowedTypos,
      prepareRecord: prepareRecord,
      scoreRecord: scoreRecord,
      searchRecords: searchRecords,
      NORMALISE: NORMALISE,
      SYNONYMS: SYNONYMS,
      MIN_CHARS: MIN_CHARS
    };
  }
})();
